/* eslint-disable react/no-unstable-nested-components */
import React, { useEffect, useMemo } from 'react';
import {
  Route,
  Switch,
  Redirect,
  BrowserRouter,
  useLocation,
} from 'react-router-dom';
import { createBrowserHistory } from 'history';
import PropTypes from 'prop-types';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { isUserAuthenticated, isTokenAuthenticated } from './utils/auth.utils';
import {
  isAdminAuthenticated,
  hasAdminPermission,
} from './utils/admin.auth.utils';
import { COMMON_LOGIN_ROUTE } from 'constant/admin.config';
import { AdminAccessDenied } from './component/Admin/AdminStateViews';
import {
  APP_SCALE,
  Basename_url,
  DESKTOP_BREAKPOINT,
} from 'constant/app.config';
import PageNotFoundFile from './component/PageNotFound/PageNotFoundFile';
import { useMediaQuery } from '@mui/material';

function useAppTheme() {
  return useMemo(
    () =>
      createTheme({
        components: {
          MuiTypography: {
            defaultProps: {
              variantMapping: {
                h1: 'h2',
                h2: 'h2',
                h3: 'h2',
                h4: 'h2',
                h5: 'h2',
                h6: 'h2',
                subtitle1: 'h2',
                subtitle2: 'h2',
                body1: 'span',
                body2: 'span',
              },
            },
          },

          MuiFormLabel: {
            styleOverrides: {
              asterisk: { color: '#db3131' },
            },
          },

          MuiTooltip: {
            defaultProps: {
              PopperProps: {
                container: () => document.getElementById('root'),
              },
            },
          },
          MuiDialog: {
            defaultProps: {
              container: () => document.getElementById('dialog-root'),
            },
            styleOverrides: {
              paper: {
                // ❌ normal dialogs → no scaling
                transform: `scale(${APP_SCALE})`,
                transformOrigin: 'center',
                maxHeight: `calc(100vh / ${APP_SCALE} - 64px)`,
              },

              // ✅ ONLY fullscreen dialogs
              paperFullScreen: {
                transform: 'none',
              },
            },
          },
        },
      }),
    []
  );
}

const history = createBrowserHistory();
function RenderLayout(props) {
  const { route } = props;
  const {
    component: Component,
    layout: Layout,
    routes,
    parentPath,
    layoutProps,
    routeConfig,
  } = route;
  if (route.layout) {
    return (
      <Layout breadcrumbs={layoutProps?.breadcrumbs} routes={routeConfig}>
        <Component props={props} routes={routes} parentPath={parentPath} />
      </Layout>
    );
  }
  return <Component props={props} routes={routes} parentPath={parentPath} />;
}

function checkRouteType(route) {
  const { routeType = null, routeConfig = '', parentPath = '' } = route;
  let renderComponent = RenderLayout;
  if (routeType) {
    renderComponent = routeType;
  }
  return (
    <Route
      path={parentPath}
      exact={route.exact}
      key={parentPath}
      render={(props) =>
        renderComponent({
          props,
          route,
          routeConfig,
          parentPath,
        })
      }
    />
  );
}

function renderChildRoutes({ routes, routeConfig, parentPath = '' }) {
  return Object.values(routes).map((route) => {
    const routePath = route.key === '/' ? '' : `/${route.key}`;
    const constructedParentPath = `${parentPath}${routePath}`;
    return checkRouteType({
      ...route,
      routeConfig,
      parentPath: constructedParentPath,
    });
  });
}

export function redirectTo(url) {
  return <Redirect to={url} />;
}
function RenderRoutes({ routes, routeConfig }) {
  return (
    <Switch>
      <Route path="/" exact component={() => redirectTo('/login')} />
      {renderChildRoutes({ routes, routeConfig })}
      <Route component={() => <div>PageNotFound</div>} />
    </Switch>
  );
}

export function RenderRoutesRecursively({ routes, parentPath }) {
  return (
    <Switch>
      {renderChildRoutes({ routes, parentPath })}
      {/* <Route component={() => <div>PageNotFound</div>} /> */}
      <Route component={PageNotFoundFile} />
    </Switch>
  );
}

export function protectedRoute({ route, props, parentPath }) {
  if (!isUserAuthenticated() || !isTokenAuthenticated()) {
    localStorage.setItem('isLogin', '');
    localStorage.removeItem('userDetails');
    localStorage.removeItem('moxoDetail');
    localStorage.removeItem('userDetail');
    localStorage.removeItem('userType');
    localStorage.removeItem('lastSeenUser');
    localStorage.removeItem('token');
    localStorage.removeItem('accesstoken');
    localStorage.removeItem('tempid');
    localStorage.removeItem('legalsliderval');
    localStorage.removeItem('propertyId');
    localStorage.removeItem('version');
    localStorage.removeItem('propertyId');
    localStorage.setItem('buyerSolicitor', '');
    localStorage.setItem('buyerSolicitorDetails', '');
    return redirectTo('/logout');
  }
  return <RenderLayout props={props} route={route} parentPath={parentPath} />;
}

/**
 * Route guard for the admin layer.
 *
 * Deliberately separate from `protectedRoute`: the admin session is namespaced
 * (see `utils/admin.auth.utils`) and an unauthenticated admin is sent to the
 * admin sign-in screen rather than through the end-user `/logout` flow, which
 * would clear the user's own session.
 */
export function adminProtectedRoute({ route, props, parentPath }) {
  if (!isAdminAuthenticated()) {
    // There is no separate admin sign-in screen — admins sign in on the
    // common retreX login page.
    return redirectTo(COMMON_LOGIN_ROUTE);
  }
  if (route.permission && !hasAdminPermission(route.permission)) {
    return (
      <RenderLayout
        props={props}
        route={{ ...route, component: AdminAccessDenied }}
        parentPath={parentPath}
      />
    );
  }
  return <RenderLayout props={props} route={route} parentPath={parentPath} />;
}

export default function AppRoutes({ routeConfig }) {
  const theme = useAppTheme();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [history.location.pathname, window.location.pathname]);
  return (
    <ThemeProvider theme={theme}>
      <BrowserRouter history={history} basename={Basename_url}>
        <RenderRoutes routes={routeConfig} routeConfig={routeConfig} />
      </BrowserRouter>
    </ThemeProvider>
  );
}

RenderLayout.propTypes = {
  route: PropTypes.instanceOf(Object).isRequired,
};
RenderRoutes.propTypes = {
  routes: PropTypes.instanceOf(Object).isRequired,
  routeConfig: PropTypes.instanceOf(Object).isRequired,
};
RenderRoutesRecursively.propTypes = {
  routes: PropTypes.instanceOf(Object).isRequired,
  parentPath: PropTypes.string.isRequired,
};
AppRoutes.propTypes = {
  routeConfig: PropTypes.instanceOf(Object).isRequired,
};
